import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '../shared/hooks/useAuth';
import { RouteGuard } from '../shared/components/RouteGuard';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { NotificationProvider } from '../shared/context/NotificationContext';
import { PolicyUpdateModal } from '../components/legal/PolicyUpdateModal';

// ── Lazy-loaded layout shells ──────────────────────────────────
const AuthLayout = lazy(() => import('./layouts/AuthLayout'));
const SuperadminLayout = lazy(() => import('./layouts/SuperadminLayout'));
const AgencyLayout = lazy(() => import('./layouts/AgencyLayout'));
const ReferrerLayout = lazy(() => import('./layouts/ReferrerLayout'));
const CandidateLayout = lazy(() => import('./layouts/CandidateLayout'));
const AccountantLayout = lazy(() => import('./layouts/AccountantLayout'));
const AccountantDashboard = lazy(() => import('../features/accountant/pages/AccountantDashboard'));
const HelpdeskLayout = lazy(() => import('./layouts/HelpdeskLayout'));
const HelpdeskDashboard = lazy(() => import('../features/helpdesk/pages/HelpdeskDashboard'));
const SupportCenterPage = lazy(() => import('../features/support/pages/SupportCenterPage'));
const SupportTicketDetailPage = lazy(() => import('../features/support/pages/SupportTicketDetailPage'));
const SupportLayout = lazy(() => import('./layouts/SupportLayout'));

// ── Auth pages ────────────────────────────────────────────────
const LoginPage = lazy(() => import('../features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('../features/auth/pages/RegisterPage'));
const PendingPage = lazy(() => import('../features/auth/pages/PendingPage'));
const SuspendedPage = lazy(() => import('../features/auth/pages/SuspendedPage'));
const UnauthorizedPage = lazy(() => import('../features/auth/pages/UnauthorizedPage'));
const SuperadminRegisterPage = lazy(() => import('../features/auth/pages/SuperadminRegisterPage'));
const VerifyPage = lazy(() => import('../features/auth/pages/VerifyPage'));

// ── Role dashboard pages (stub — expanded in Phase 2) ─────────
const SuperadminDashboard = lazy(() => import('../features/admin/pages/SuperadminDashboard'));
const SuperadminDirectory = lazy(() => import('../features/admin/pages/SuperadminDirectory'));
const SuperadminJobs = lazy(() => import('../features/admin/pages/SuperadminJobs'));

const SuperadminFinance = lazy(() => import('../features/admin/pages/SuperadminFinance'));
const SuperadminReferrals = lazy(() => import('../features/admin/pages/SuperadminReferrals'));
const SuperadminSettings = lazy(() => import('../features/admin/pages/SuperadminSettings'));
const VisaMasterPage = lazy(() => import('../features/admin/pages/VisaMasterPage'));
const AuditCompliancePage = lazy(() => import('../features/admin/pages/AuditCompliancePage'));
const AgencyDashboard = lazy(() => import('../features/jobs/pages/AgencyDashboard'));
const ReferrerDashboard = lazy(() => import('../features/referrals/pages/ReferrerDashboard'));
const CandidateDashboard = lazy(() => import('../features/applications/pages/CandidateDashboard'));
const EmployerPortalPage = lazy(() => import('../features/employer/pages/EmployerPortalPage'));
const LandingPage = lazy(() => import('../features/landing/LandingPage'));
const PublicPolicyPage = lazy(() => import('../features/legal/pages/PublicPolicyPage'));

// ── Agency specific sub-pages ──────────────────────────────────
const EmployerDirectory = lazy(() => import('../features/jobs/pages/EmployerDirectory'));
const JobList = lazy(() => import('../features/jobs/pages/JobList'));
const JobWizard = lazy(() => import('../features/jobs/pages/JobWizard'));
const CandidatePipeline = lazy(() => import('../features/applications/pages/CandidatePipeline'));
const InterviewScheduler = lazy(() => import('../features/interviews/pages/InterviewScheduler'));
const VisaTracker = lazy(() => import('../features/visa/pages/VisaTracker'));
const TravelCoordinator = lazy(() => import('../features/travel/pages/TravelCoordinator'));
const WalletDashboard = lazy(() => import('../features/wallet/pages/WalletDashboard'));
const SettingsPage = lazy(() => import('../features/settings/pages/SettingsPage'));
const JobMarketplace = lazy(() => import('../features/jobs/pages/JobMarketplace'));
const AgencyDocuments = lazy(() => import('../features/documents/pages/AgencyDocuments'));
const ProfilePage = lazy(() => import('../features/profile/pages/ProfilePage'));
const CandidateDocumentsPage = lazy(() => import('../features/profile/pages/CandidateDocumentsPage'));

// ── Referrer specific sub-pages ──────────────────────────────────
const ReferralCenter = lazy(() => import('../features/referrals/pages/ReferralCenter'));
const AgencyDirectory = lazy(() => import('../features/referrals/pages/AgencyDirectory'));
const AgencyPerformance = lazy(() => import('../features/referrals/pages/AgencyPerformance'));
const CommissionCenter = lazy(() => import('../features/referrals/pages/CommissionCenter'));
const NotificationsPage = lazy(() => import('../features/notifications/pages/NotificationsPage'));
const NotFoundPage = lazy(() => import('../shared/components/NotFoundPage'));

function NetworkStatusBanner() {
 const [isOffline, setIsOffline] = React.useState(!navigator.onLine);

 React.useEffect(() => {
 const handleOnline = () => setIsOffline(false);
 const handleOffline = () => setIsOffline(true);
 window.addEventListener('online', handleOnline);
 window.addEventListener('offline', handleOffline);
 return () => {
 window.removeEventListener('online', handleOnline);
 window.removeEventListener('offline', handleOffline);
 };
 }, []);

 if (!isOffline) return null;

 return (
 <div style={{
 position: 'fixed',
 top: 0,
 left: 0,
 right: 0,
 zIndex: 99999,
 backgroundColor: '#ef4444',
 color: '#ffffff',
 padding: '8px 16px',
 fontSize: '13px',
 fontWeight: 600,
 textAlign: 'center',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 gap: '12px',
 boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
 }}>
 <span> Internet Connection Interrupted — Reconnecting automatically when online...</span>
 <button 
 onClick={() => window.location.reload()}
 style={{ background: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '2px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
 >
 Retry
 </button>
 </div>
 );
}

function GlobalLegalWatcher() {
 const { firebaseUser, userDoc } = useAuth();
 if (!firebaseUser || !userDoc) return null;
 return <PolicyUpdateModal uid={firebaseUser.uid} userDoc={userDoc} />;
}

export default function App() {
 return (
 <BrowserRouter>
 <AuthProvider>
 <NotificationProvider>
 <NetworkStatusBanner />
 <GlobalLegalWatcher />
 <Suspense fallback={<LoadingScreen />}>
 <Routes>
 {/* ── Public auth routes ────────────────────── */}
 <Route path="/auth" element={<AuthLayout />}>
 <Route index element={<Navigate to="login" replace />} />
 <Route path="login" element={<LoginPage />} />
 <Route path="superadmin-register" element={<SuperadminRegisterPage />} />
 <Route path="register" element={<RegisterPage />} />
 <Route path="pending" element={<PendingPage />} />
 <Route path="verify" element={<VerifyPage />} />
 <Route path="suspended" element={<SuspendedPage />} />
 </Route>

 <Route path="/unauthorized" element={<UnauthorizedPage />} />

 {/* ── Public Legal Policy Routes ───────────── */}
 <Route path="/terms" element={<PublicPolicyPage tab="terms" />} />
 <Route path="/privacy" element={<PublicPolicyPage tab="privacy" />} />
 <Route path="/refund-policy" element={<PublicPolicyPage tab="refund" />} />
 <Route path="/placement-guarantee" element={<PublicPolicyPage tab="placement" />} />
 <Route path="/affiliate-terms" element={<PublicPolicyPage tab="referrer" />} />
 <Route path="/about-us" element={<PublicPolicyPage tab="about" />} />


 {/* ── Superadmin ────────────────────────────── */}
 <Route
 path="/superadmin"
 element={
 <RouteGuard allowedRoles={['superadmin']}>
 <SuperadminLayout />
 </RouteGuard>
 }
 >
 <Route index element={<SuperadminDashboard />} />
 <Route path="directory" element={<SuperadminDirectory />} />
 <Route path="jobs" element={<SuperadminJobs />} />
 <Route path="visa-master" element={<VisaMasterPage />} />
 <Route path="audit-compliance" element={<AuditCompliancePage />} />
 <Route path="finance" element={<SuperadminFinance />} />
 <Route path="helpdesk" element={<HelpdeskDashboard initialTab="unassigned" />} />
 <Route path="helpdesk/tickets/:ticketId" element={<HelpdeskDashboard initialTab="active" />} />
 <Route path="referrals" element={<SuperadminReferrals />} />
 <Route path="settings" element={<SuperadminSettings />} />
 <Route path="notifications" element={<NotificationsPage />} />
 </Route>

 {/* ── Accountant ──────────────────────────────── */}
 <Route
 path="/accountant"
 element={
 <RouteGuard allowedRoles={['accountant', 'superadmin']}>
 <AccountantLayout />
 </RouteGuard>
 }
 >
 <Route index element={<AccountantDashboard />} />
 <Route path="invoices" element={<AccountantDashboard />} />
 <Route path="beneficiaries" element={<AccountantDashboard />} />
 <Route path="settlements" element={<AccountantDashboard />} />
 <Route path="manual-payments" element={<AccountantDashboard />} />
 <Route path="ledgers" element={<AccountantDashboard />} />
 <Route path="notifications" element={<NotificationsPage />} />
 </Route>

 {/* ── Helpdesk Support Desk ────────────────────── */}
 <Route
   path="/helpdesk"
   element={
     <RouteGuard allowedRoles={['helpdesk', 'superadmin']}>
       <HelpdeskLayout />
     </RouteGuard>
   }
 >
   <Route index element={<HelpdeskDashboard initialTab="unassigned" />} />
   <Route path="active" element={<HelpdeskDashboard initialTab="active" />} />
   <Route path="waiting" element={<HelpdeskDashboard initialTab="waiting" />} />
   <Route path="resolved" element={<HelpdeskDashboard initialTab="resolved" />} />
   <Route path="tickets/:ticketId" element={<HelpdeskDashboard initialTab="active" />} />
 </Route>

 {/* ── User Support Center (All Authenticated Roles) ─ */}
 <Route
   path="/support"
   element={
     <RouteGuard allowedRoles={['candidate', 'agency', 'agency_staff', 'employer', 'referrer', 'superadmin', 'accountant', 'helpdesk']}>
       <SupportLayout />
     </RouteGuard>
   }
 >
   <Route index element={<SupportCenterPage />} />
   <Route path=":ticketId" element={<SupportTicketDetailPage />} />
 </Route>

 {/* ── Agency ────────────────────────────────── */}
 <Route
 path="/agency"
 element={
 <AgencyOnboardRedirect>
 <RouteGuard allowedRoles={['agency', 'agency_staff']} requireVerified={true}>
 <AgencyLayout />
 </RouteGuard>
 </AgencyOnboardRedirect>
 }
 >
 <Route index element={<AgencyDashboard />} />
 <Route path="employers" element={<EmployerDirectory />} />
 <Route path="jobs" element={<JobList />} />
 <Route path="jobs/new" element={<JobWizard />} />
 <Route path="jobs/create" element={<JobWizard />} />
 <Route path="jobs/edit/:jobId" element={<JobWizard />} />
 <Route path="candidates" element={<CandidatePipeline />} />
 <Route path="interviews" element={<InterviewScheduler />} />
 <Route path="visa" element={<VisaTracker />} />
 <Route path="travel" element={<TravelCoordinator />} />
 <Route path="wallet" element={<WalletDashboard />} />
 <Route path="performance" element={<AgencyPerformance />} />
 <Route path="profile" element={<Navigate to="/agency/settings" replace />} />
 <Route path="settings" element={<SettingsPage />} />
 <Route path="staff" element={<Navigate to="/agency/settings" replace />} />
 <Route path="documents" element={<AgencyDocuments />} />
 <Route path="notifications" element={<NotificationsPage />} />
 <Route path="applications/:applicationId" element={<Navigate to="/agency/candidates" replace />} />
 <Route path="pipeline" element={<Navigate to="/agency/candidates" replace />} />
 </Route>

 {/* ── Employer & Employee ──────────────────── */}
 {/* EmployerPortalPage renders full sidebar/nav layout */}
 <Route
 path="/employer"
 element={
 <RouteGuard allowedRoles={['employer']} requireVerified={true}>
 <EmployerPortalPage />
 </RouteGuard>
 }
 />
 <Route
 path="/employer/notifications"
 element={
 <RouteGuard allowedRoles={['employer']} requireVerified={true}>
 <EmployerPortalPage />
 </RouteGuard>
 }
 />
 <Route path="/employee" element={<Navigate to="/employer" replace />} />
 <Route
 path="/employee/notifications"
 element={
 <RouteGuard allowedRoles={['employer']} requireVerified={true}>
 <EmployerPortalPage />
 </RouteGuard>
 }
 />
  <Route
    path="/notifications"
    element={
      <RouteGuard allowedRoles={['superadmin', 'accountant', 'agency', 'employer', 'referrer', 'candidate']}>
        <NotificationsPage />
      </RouteGuard>
    }
  />

 <Route path="/referee" element={<RefereeOnboardRedirect />} />

 {/* ── Referrer ──────────────────────────────── */}
 <Route
 path="/referrer"
 element={
 <RouteGuard allowedRoles={['referrer']} requireVerified={true}>
 <ReferrerLayout />
 </RouteGuard>
 }
 >
 <Route index element={<ReferrerDashboard />} />
 <Route path="center" element={<ReferralCenter />} />
 <Route path="agencies" element={<AgencyDirectory />} />
 <Route path="agencies/:agencyId" element={<AgencyPerformance />} />
 <Route path="commissions" element={<CommissionCenter />} />
 <Route path="profile" element={<ProfilePage />} />
 <Route path="notifications" element={<NotificationsPage />} />
 </Route>

 {/* ── Candidate ─────────────────────────────── */}
 <Route
 path="/candidate"
 element={
 <RouteGuard allowedRoles={['candidate']}>
 <CandidateLayout />
 </RouteGuard>
 }
 >
 <Route index element={<CandidateDashboard />} />
 <Route path="jobs" element={<JobMarketplace />} />
 <Route path="tracker" element={<Navigate to="/candidate" replace />} />
 <Route path="applications/:applicationId" element={<Navigate to="/candidate" replace />} />
 <Route path="history" element={<CandidateDashboard defaultShowHistoryOnly={true} />} />
 <Route path="profile" element={<ProfilePage />} />
 <Route path="documents" element={<CandidateDocumentsPage />} />
 <Route path="notifications" element={<NotificationsPage />} />
 </Route>

 {/* ── Top-level /profile smart redirect ────── */}
 <Route path="/profile" element={<ProfileRedirect />} />

 {/* ── Root & Fallback routes ─────────────────── */}
 <Route path="/" element={<LandingPage />} />
 <Route path="*" element={<NotFoundPage />} />
 </Routes>
 </Suspense>
 </NotificationProvider>
 </AuthProvider>
 </BrowserRouter>
 );
}

function ProfileRedirect() {
  const { role, isLoading, firebaseUser } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!firebaseUser) return <Navigate to="/auth/login" replace />;
  if (role === 'accountant') return <Navigate to="/accountant" replace />;
  if (role === 'helpdesk') return <Navigate to="/helpdesk" replace />;
  if (role === 'candidate') return <Navigate to="/candidate/profile" replace />;
  if (role === 'referrer') return <Navigate to="/referrer/profile" replace />;
  if (role === 'agency' || role === 'agency_staff') return <Navigate to="/agency/settings" replace />;
  if (role === 'employer') return <Navigate to="/employer" replace />;
  if (role === 'superadmin') return <Navigate to="/superadmin/settings" replace />;
  return <Navigate to="/auth/login" replace />;
}

function RootRedirect() {
  const { role, isLoading, firebaseUser } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!firebaseUser) return <Navigate to="/auth/login" replace />;

  const roleRoutes: Record<string, string> = {
    superadmin: '/superadmin',
    accountant: '/accountant',
    helpdesk: '/helpdesk',
    agency: '/agency',
    agency_staff: '/agency',
    employer: '/employer',
    referrer: '/referrer',
    candidate: '/candidate',
  };

  return <Navigate to={roleRoutes[role ?? ''] ?? '/auth/login'} replace />;
}

function RefereeOnboardRedirect() {
 const [searchParams] = useSearchParams();
 const invite = searchParams.get('invite');
 const { firebaseUser } = useAuth();

 if (firebaseUser) {
 return <Navigate to="/referrer" replace />;
 }

 if (invite) {
 return <Navigate to={`/auth/register?invite=${invite}`} replace />;
 }

 return (
 <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0f172a', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
 <div style={{ width: '100%', maxWidth: '500px', padding: '40px 32px', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', textAlign: 'center', color: '#f8fafc', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }}>
 <div style={{ fontSize: '4rem', marginBottom: '20px' }}></div>
 <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '12px' }}>Referee Onboarding Restricted</h2>
 <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '24px' }}>
 Referee registration is invite-only. You must use an invitation link generated by the SuperAdmin.
 </p>
 <NavigateButton label="Back to Sign In" to="/auth/login" />
 </div>
 </div>
 );
}

function AgencyOnboardRedirect({ children }: { children: React.ReactNode }) {
 const [searchParams] = useSearchParams();
 const refCode = searchParams.get('ref');
 const invite = searchParams.get('invite');
 const { firebaseUser, isLoading } = useAuth();

 if (isLoading) {
 return <LoadingScreen />;
 }

 if (!firebaseUser) {
 if (refCode) {
 return <Navigate to={`/auth/register?role=agency&ref=${refCode}`} replace />;
 }
 if (invite) {
 return <Navigate to={`/auth/register?invite=${invite}`} replace />;
 }
 return (
 <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0f172a', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
 <div style={{ width: '100%', maxWidth: '500px', padding: '40px 32px', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', textAlign: 'center', color: '#f8fafc', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)' }}>
 <div style={{ fontSize: '4rem', marginBottom: '20px' }}></div>
 <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '12px' }}>Agency Onboarding Restricted</h2>
 <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.6, marginBottom: '24px' }}>
 Agency onboarding is restricted. You can only register using a referral link sent by a Referee or SuperAdmin.
 </p>
 <NavigateButton label="Back to Sign In" to="/auth/login" />
 </div>
 </div>
 );
 }

 return <>{children}</>;
}

function NavigateButton({ label, to }: { label: string; to: string }) {
 return (
 <a href={to} style={{ display: 'inline-block', padding: '12px 24px', background: '#10b981', color: '#fff', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', transition: 'background 0.2s' }}>
 {label}
 </a>
 );
}


